import type { GamePhase } from "@cloudnest/horror-race-engine";

const PHASE_LABELS: Record<GamePhase, string> = {
  SETUP_CHARACTER_SELECTION: "캐릭터 선택",
  ROUND_DRAW_AND_PLACE: "코인 뽑기 & 배치",
  ROUND_RESOLVE: "라운드 결산",
  ROUND_RECAP: "라운드 정산",
  RACE_SCORING: "레이스 결산",
  GAME_OVER: "게임 종료",
};

interface RoundInfoProps {
  readonly phase: GamePhase;
  readonly raceNumber: number;
  readonly totalRaces: number;
  readonly round: number;
  /** Coins still in the round pool (undrawn) — shown as "남은 코인 N개". */
  readonly poolSize: number;
  /**
   * Render the 라운드 slot as a ghost (label-only, dim + dotted underline) — used
   * when the slot is structurally present but the value isn't meaningful for the
   * current phase (e.g. SETUP_CHARACTER_SELECTION, RACE_SCORING). Default false.
   */
  readonly roundGhost?: boolean;
  /**
   * Render the 남은 코인 slot as a ghost — used when coins aren't meaningful
   * (e.g. SETUP_CHARACTER_SELECTION, ROUND_RECAP, RACE_SCORING). Default false.
   */
  readonly poolGhost?: boolean;
  /**
   * M1: when provided, the 남은 코인 slot is promoted from a `<span>` to a
   * `<button>` that invokes this callback. Opens the coin-memory sheet.
   * Affordance = border + bg only (project rule: no icon for clickability).
   */
  readonly onOpenMemory?: () => void;
  /**
   * M1: gates the memory button. When false (e.g. SETUP_CHARACTER_SELECTION
   * with no draws recorded yet), the button renders disabled (dotted faded
   * border + cursor: not-allowed). Only consulted when `onOpenMemory` is set.
   */
  readonly memoryEnabled?: boolean;
}

const GHOST_STYLE = {
  opacity: 0.32,
  borderBottom: "1px dotted currentColor",
  paddingBottom: "1px",
} as const;

// M1 — button affordance for the 남은 코인 slot. Border + bg only, NO icon.
// Active = solid mauve border + tinted bg; ghost = dashed dim border; disabled
// = dotted faded border + not-allowed cursor.
const POOL_BTN_BASE_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: "999px",
  fontFamily: "ui-monospace, monospace",
  fontSize: "inherit",
  lineHeight: "inherit",
  background: "transparent",
  color: "inherit",
  cursor: "pointer",
  transition: "background 0.14s ease, border-color 0.14s ease",
} as const;

/**
 * T20 — compact one-line race/round info. The current-turn player and the 2-pass
 * draw progress are read from the player rail, so this no longer names the
 * current player or shows a 추가 뽑기 (2/2) draw-pass badge.
 *
 * D3 fixed top frame — each non-applicable slot is rendered as a ghost (label
 * only, dim + dotted underline) instead of being hidden, so the 4-slot footprint
 * is preserved across phases and the player rail Y stays fixed.
 *
 * Fixed height rule — container locks to one line via `min-h` + `overflow-hidden`,
 * left phase label truncates with ellipsis (`min-w-0` + `truncate`), and the right
 * slot group stays on a single line (`shrink-0` + `whitespace-nowrap`). Without
 * this, narrow viewports + long phase labels (`코인 뽑기 & 배치`) or longer leave
 * button (`관전 나가기`) would wrap the row to 2–3 lines and push the player rail
 * down, breaking the D3 invariant. See wireframe top-frame.html ⑥.
 */
export function RoundInfo({
  phase,
  raceNumber,
  totalRaces,
  round,
  poolSize,
  roundGhost = false,
  poolGhost = false,
  onOpenMemory,
  memoryEnabled = false,
}: RoundInfoProps) {
  return (
    <div
      className="flex min-h-[2.625rem] items-center justify-between gap-2 overflow-hidden rounded-lg px-4 py-2.5 text-sm"
      style={{
        backgroundColor: "var(--cn-horror-card-bg)",
        border: "1px solid var(--cn-horror-surface-border)",
      }}
    >
      <div
        data-testid="horror-phase-label"
        className="min-w-0 flex-1 truncate whitespace-nowrap font-bold"
        style={{ color: "var(--cn-horror-round-text)" }}
        title={PHASE_LABELS[phase]}
      >
        {PHASE_LABELS[phase]}
      </div>
      <div
        className="flex shrink-0 items-center gap-3 whitespace-nowrap"
        style={{ color: "var(--cn-horror-text-muted)" }}
      >
        <span data-testid="horror-turn-badge">
          레이스 {raceNumber}/{totalRaces}
        </span>
        {roundGhost ? (
          <span
            data-testid="horror-round-slot"
            data-ghost="true"
            title="라운드 — 진행 중 표시 자리"
            style={GHOST_STYLE}
          >
            라운드
          </span>
        ) : (
          <span data-testid="horror-round-slot">{round}라운드</span>
        )}
        {renderPoolSlot({
          poolGhost,
          poolSize,
          onOpenMemory,
          memoryEnabled,
        })}
      </div>
    </div>
  );
}

interface PoolSlotArgs {
  readonly poolGhost: boolean;
  readonly poolSize: number;
  readonly onOpenMemory?: () => void;
  readonly memoryEnabled: boolean;
}

function renderPoolSlot({
  poolGhost,
  poolSize,
  onOpenMemory,
  memoryEnabled,
}: PoolSlotArgs) {
  const label = poolGhost ? "남은 코인" : `남은 코인 ${poolSize}개`;

  // No onOpenMemory → legacy span branch, back-compat for callers (tests etc.)
  // that don't wire up the memory feature.
  if (onOpenMemory === undefined) {
    if (poolGhost) {
      return (
        <span
          data-testid="horror-coin-count"
          data-ghost="true"
          title="남은 코인 — 진행 중 표시 자리"
          style={GHOST_STYLE}
        >
          남은 코인
        </span>
      );
    }
    return <span data-testid="horror-coin-count">남은 코인 {poolSize}개</span>;
  }

  // M1 button branch. Three visual states driven by memoryEnabled × poolGhost:
  //   active  = solid mauve border, tinted bg
  //   ghost   = dashed dim border, no fill
  //   disabled = dotted faded border, cursor: not-allowed
  const disabled = !memoryEnabled;
  const variantStyle: React.CSSProperties = disabled
    ? {
        border: "1px dotted var(--cn-horror-text-muted)",
        opacity: 0.32,
        cursor: "not-allowed",
      }
    : poolGhost
      ? {
          border: "1px dashed var(--cn-horror-text-muted)",
          opacity: 0.6,
        }
      : {
          border: "1px solid var(--cn-horror-p3)",
          background:
            "color-mix(in srgb, var(--cn-horror-p3) 14%, transparent)",
        };

  return (
    <button
      type="button"
      data-testid="horror-coin-count"
      data-ghost={poolGhost ? "true" : undefined}
      data-disabled={disabled ? "true" : undefined}
      onClick={disabled ? undefined : onOpenMemory}
      disabled={disabled}
      aria-haspopup="dialog"
      aria-label={
        disabled ? "라운드 토큰 메모리 (기록 없음)" : "라운드 토큰 메모리 열기"
      }
      title={
        poolGhost
          ? "남은 코인 — 진행 중 표시 자리 (클릭 시 메모리 시트)"
          : undefined
      }
      style={{ ...POOL_BTN_BASE_STYLE, ...variantStyle }}
    >
      {label}
    </button>
  );
}
